import React from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, FormikErrors, FormikValues } from "formik";
import { useReportForm } from "../hooks/useReportForm";
import FormDropDown from "../../common/layout/form/FormDropDown";
import NewFormField from "../../common/layout/form/NewFormField";
import { divider } from "../../auth/styles/fieldsContainer.styles";
import { RootStateOrAny, useSelector } from "react-redux";
import NewFormBtns from "../../common/layout/form/NewFormBtns";
import NewFormCheckbox from "../../common/layout/form/NewFormCheckbox";
import { IReportParameter } from "../model/report.types";
import ReportReadOnlyDetails from "./ReportReadOnlyDetails";
import { reportParameterType } from "../model/reportParameterType.enum";
import ClientsDropDown from "../../orders/components/forms/newOrder/ClientsDropDown";
import VehiclesDropDown from "./VehiclesDropDown";
import { Icon, IPivotItemProps, Label, Pivot, PivotItem } from "office-ui-fabric-react";
import { littleWrapper, mainWrapper, paddingTopStyle, wrapper } from "../styles/style";
import { IReportColumn } from "../interfaces/IReportColumn";
import ActivityIndicator from "../../common/layout/ActivityIndicator";
import FormDatePicker from "../../common/layout/form/FormDatePicker";
import i18next from "i18next";

const ReportForm: React.FC = () => {
    const { t } = useTranslation("admin");
    const {
        loading,
        cancelForm,
        handleSubmit,
        initValues,
        orderTypes,
        operators,
        availableYears
    } = useReportForm()

    const { reportDetails } = useSelector(
        (state: RootStateOrAny) => state.reports
    );

    const columnsToSort = reportDetails.reportColumns ? reportDetails.reportColumns.map((reportColumn: IReportColumn) => {
        return {
            key: reportColumn.name,
            text: reportColumn.translation
        }
    }) : [];

    var columnsToGroup = reportDetails.reportColumns ? reportDetails.reportColumns.filter((col: IReportColumn) => col.canBeGrouped == true) : []

    const columnsToGroupPrepared = columnsToGroup.map((reportColumn: IReportColumn) => {
        return {
            key: reportColumn.name,
            text: reportColumn.translation
        }
    })

    const isColumnsToSort = columnsToSort.length > 0 ? true : false;

    const reportColumns = reportDetails.reportColumns ? reportDetails.reportColumns.map((reportColumn: IReportColumn) => checkcolumn(reportColumn)) : [];

    const IsError = (obj: FormikErrors<FormikValues>) => Object.values(obj).some(v => v !== null && typeof v !== "undefined")

    function _customRenderer(
        link?: IPivotItemProps,
        defaultRenderer?: (link?: IPivotItemProps) => JSX.Element | null,
    ): JSX.Element | null {
        if (!link || !defaultRenderer) {
            return null;
        }

        return (
            <span style={{ flex: '0 0 100%' }}>
                {defaultRenderer({ ...link, itemIcon: undefined })}
                <Icon iconName={link.itemIcon} style={{ color: 'red', marginLeft: '3px' }} />
            </span>
        );
    }

    return (
        loading ? <ActivityIndicator text={t("nav.generatingReport") as string} /> :
            <div >
                <ReportReadOnlyDetails />
                <Formik
                    validate={(values) => {
                        const errors: any = {}
                        reportDetails.reportParameters.map((reportParameter: IReportParameter) => {
                            if (reportParameter.isRequired == true) {
                                if (values[reportParameter.name] == undefined) {
                                    errors[(reportParameter.name)] = t("nav.fieldRequired")
                                }
                            }
                        })
                        return errors;
                    }}
                    enableReinitialize={true}
                    initialValues={initValues}
                    onSubmit={handleSubmit}
                >
                    {({ handleSubmit, setFieldValue, errors }) => (
                        <Form onSubmit={handleSubmit}>
                            <Pivot >
                                <PivotItem headerText={t("nav.panelBasicSettings") as string} itemIcon={IsError(errors) == true ? "AlertSolid" : undefined} onRenderItemLink={_customRenderer}>
                                    {
                                        reportDetails.reportParameters ?
                                            reportDetails.reportParameters.map((reportParameter: IReportParameter) => switchInputType(reportParameter, orderTypes, operators, availableYears, setFieldValue))
                                            : <></>}
                                    {isColumnsToSort ?
                                        <div>
                                            <FormDropDown
                                                name={"isDesc"}
                                                headerText={t("nav.panelSortBy") as string}
                                                placeholder={t("nav.descending") as string}
                                                options={[{ key: true, text: t("nav.descending") as string },
                                                { key: false, text: t("nav.ascending") as string }]}
                                                required={false}
                                            />
                                            <FormDropDown
                                                name={"orderBy"}
                                                headerText={t("nav.panelSortByColumn") as string}
                                                options={columnsToSort}
                                                required={false}
                                            />
                                        </div>
                                        : <></>}
                                    <br></br>
                                </PivotItem>
                                <PivotItem headerText={t("nav.panelAdvancedSettings") as string}>
                                    {reportColumns.length > 0 ?
                                        <>
                                            <div className={mainWrapper}>
                                                <Label>{t("nav.selectReportColumns") as string}</Label>
                                                {reportColumns}
                                            </div>
                                            {columnsToGroup.length > 0 ?
                                                <div className={mainWrapper}>
                                                    <Label>{t("nav.selectGroupByColumns") as string}</Label>
                                                    <FormDropDown
                                                        name={"FirstGroupBy"}
                                                        headerText={t("nav.panelGroupByColumn1") as string}
                                                        options={columnsToGroupPrepared}
                                                        required={false}
                                                    />
                                                    {columnsToGroup.length > 1 ?
                                                        <FormDropDown
                                                            name={"SecondGroupBy"}
                                                            headerText={t("nav.panelGroupByColumn2") as string}
                                                            options={columnsToGroupPrepared}
                                                            required={false}
                                                        />
                                                        : <></>}
                                                </div>
                                                : <></>}
                                        </>
                                        :
                                        <div className={mainWrapper}>
                                            <Label>{t("nav.noAdvancedSettings") as string}</Label>
                                        </div>
                                    }
                                </PivotItem>
                            </Pivot >
                            <NewFormBtns
                                submitBtnText={t("nav.download") as string}
                                cancelForm={cancelForm}
                                disabled={loading}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
    )
};

const checkcolumn = (reportColumn: IReportColumn) => {
    return <div className={littleWrapper}>
        <NewFormCheckbox name={reportColumn.name} label={reportColumn.translation} />
    </div>
}

const switchInputType = (reportParameter: IReportParameter, orderTypes: any, operators: any, availableYears:any, setFieldValue: any) => {
    switch (reportParameter.type) {
        case reportParameterType.Numerical:
            return <div className={divider}>
                <NewFormField
                    name={reportParameter.name}
                    label={reportParameter.label}
                    type={"number"}
                    required={reportParameter.isRequired} 
                    min={"0"}
                    />
            </div>
        case reportParameterType.Date:
            return <div className={divider}>
                <FormDatePicker
                    name={reportParameter.name}
                    label={reportParameter.label}
                    required={reportParameter.isRequired}
                    placeholder={i18next.t("nav.selectDate", { ns: "admin" }) as string}
                    values={null} />
            </div>
        case reportParameterType.String:
            return <div className={divider}>
                <NewFormField
                    name={reportParameter.name}
                    label={reportParameter.label}
                    required={reportParameter.isRequired} />
            </div>
        case reportParameterType.Companies:
            return <div className={paddingTopStyle}>
                <ClientsDropDown
                    name={reportParameter.name}
                    label={reportParameter.label}
                    setFieldValue={setFieldValue}
                    resetForm={() => { }}
                    value={""}
                    required={reportParameter.isRequired}
                />
            </div>
        case reportParameterType.RecykleOperators:
            return <FormDropDown
                name={reportParameter.name}
                headerText={reportParameter.label}
                options={operators}
                required={reportParameter.isRequired}
            />
        case reportParameterType.OrderTypes:
            return <FormDropDown
                name={reportParameter.name}
                headerText={reportParameter.label}
                options={orderTypes ? orderTypes : []}
                required={reportParameter.isRequired}
            />
        case reportParameterType.Vehicles:
            return <div className={paddingTopStyle}>
                <VehiclesDropDown
                    value={""}
                    label={reportParameter.label}
                    name={reportParameter.name}
                    setFieldValue={setFieldValue} />
            </div>
        case reportParameterType.Year: {
            return <FormDropDown
                name={reportParameter.name}
                headerText={reportParameter.label}
                options={availableYears}
                required={reportParameter.isRequired}
            />
        }
        default:
            return <> </>;
    }
}

export default ReportForm;